import Vue, { VNode, CreateElement, VNodeData } from 'vue';
import DetailPagePopup from '@/views_v2/apply-product/components/rights/zhenxiang-card/components/rights-popup';
// 定义组件实例类型（根据实际 props 和 data 补充）
type DetailPagePopupInstance = Vue & {
  isVisiblePopup: boolean;
  rightsUrl: string;
  popupTitle: string;
  fullScreen: boolean;
  showUnopenedDetailUrl: boolean;
};

interface DetailPopupParams {
  /** 权益详情URL */
  rightsUrl?: string;
  /** 弹窗标题 */
  popupTitle?: string;
  /** 是否全屏 */
  fullScreen?: boolean;
  /** 是否显示未开通详情URL */
  showUnopenedDetailUrl?: boolean;
  /** 是否显示关闭按钮 */
  showClose?: boolean;
  /** 关闭回调 */
  onClose?: () => void;
  /** 关闭状态改变回调 */
  onChangeClose?: (closeStatus: boolean) => void;
  /** 自定义 header slot 内容 */
  headerSlot?: (h: CreateElement) => VNode;
}

interface PopupResult {
  success: boolean;
  action: string;
}

/**
 * 权益详情弹窗服务类
 */
class DetailPopupService {
  private instance: Vue | null = null;

  private resolvePromise: ((value: PopupResult) => void) | null = null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private rejectPromise: ((reason?: any) => void) | null = null;

  /**
   * 显示弹窗
   * @param params 弹窗参数
   * @returns Promise<PopupResult> 返回用户操作结果
   */
  public show(params: DetailPopupParams): Promise<PopupResult> {
    const {
      rightsUrl = '',
      popupTitle = '',
      fullScreen = false,
      showUnopenedDetailUrl = false,
      onClose,
      onChangeClose,
      headerSlot,
    } = params;

    return new Promise((resolve, reject) => {
      this.resolvePromise = resolve;
      this.rejectPromise = reject;

      // 创建弹窗实例
      if (!this.instance) {
        try {
          this.instance = new Vue({
            render: (h) => {
              const data: VNodeData = {
                props: {
                  rightsUrl,
                  popupTitle,
                  fullScreen,
                  showUnopenedDetailUrl,
                  showClose: true,
                  value: true,
                },
                on: {
                  change: (value: boolean) => {
                    if (!value) {
                      this.hide();
                    }
                  },
                  close: () => {
                    this.hide();
                    if (onClose) {
                      onClose();
                    }
                    resolve({ success: true, action: 'close' });
                  },
                  onChangeClose: (closeStatus: boolean) => {
                    if (onChangeClose) {
                      onChangeClose(closeStatus);
                    }
                  },
                },
              };

              if (headerSlot) {
                const headerVNode = headerSlot(h);
                data.scopedSlots = {
                  header: () => (Array.isArray(headerVNode) ? headerVNode : [headerVNode]),
                };
              }

              return h(DetailPagePopup, data);
            },
          });

          // 挂载到DOM
          const mountedDom = document.createElement('div');
          document.body.appendChild(mountedDom);
          this.instance.$mount(mountedDom);
          // 修复首次点击时，弹窗无法响应
          // 修改第106行附近的代码如下：

          if (this.instance.$children && this.instance.$children.length) {
            const childComponent = this.instance.$children[0] as DetailPagePopupInstance;
            childComponent.isVisiblePopup = true; // 现在 TypeScript 能正确识别该属性
          }
        } catch (error) {
          reject(new Error('弹窗创建失败'));
        }
      } else {
        // 更新参数并显示
        if (this.instance.$children && this.instance.$children.length) {
          const childComponent = this.instance.$children[0] as DetailPagePopupInstance;
          childComponent.rightsUrl = rightsUrl;
          childComponent.popupTitle = popupTitle;
          childComponent.fullScreen = fullScreen;
          childComponent.showUnopenedDetailUrl = showUnopenedDetailUrl;
          childComponent.isVisiblePopup = true;
        }
      }
    });
  }

  /**
   * 隐藏弹窗
   */
  public hide(): void {
    if (this.instance) {
      if (this.instance.$children && this.instance.$children.length) {
        const childComponent = this.instance.$children[0] as DetailPagePopupInstance;
        childComponent.isVisiblePopup = false;
      }

      // 延迟销毁实例，避免闪烁
      setTimeout(() => {
        if (this.instance && this.instance.$el && this.instance.$el.parentNode) {
          this.instance.$el.parentNode.removeChild(this.instance.$el);
        }
        if (this.instance) {
          this.instance.$destroy();
        }
        this.instance = null;
      }, 300);
    }

    // 如果有未处理的Promise，resolve它
    if (this.resolvePromise) {
      this.resolvePromise({ success: false, action: 'hide' });
      this.resolvePromise = null;
    }
  }

  /**
   * 销毁弹窗实例
   */
  public destroy(): void {
    if (this.instance) {
      this.hide();
      this.instance = null;
    }
  }
}

// 创建单例
const detailPopupService = new DetailPopupService();

// 导出服务实例和便捷方法
export default detailPopupService;

/**
 * 便捷的show方法 - 显示权益详情弹窗
 * @param params 弹窗参数
 * @returns Promise<PopupResult>
 */
export const showDetailPopup = (params: DetailPopupParams):
Promise<PopupResult> => detailPopupService.show(params);

/**
 * 便捷的hide方法 - 隐藏权益详情弹窗
 */
export const hideDetailPopup = (): void => {
  detailPopupService.hide();
};

/**
 * 便捷的destroy方法 - 销毁权益详情弹窗
 */
export const destroyDetailPopup = (): void => {
  detailPopupService.destroy();
};
